<script setup>
import KeywordManagementItemTable from '@frontJs/components/tables/projectManagement/sea/KeywordManagementItemTable.vue';
import { router } from '@inertiajs/vue3';
import DeleteAction from '@js/components/actions/DeleteAction.vue';
import DisplayVariablesAction from '@js/components/actions/DisplayVariablesAction.vue';
import ActionButton from '@js/components/buttons/ActionButton.vue';
import InputField from '@js/components/fields/InputField.vue';
import SwitchField from '@js/components/fields/SwitchField.vue';
import TextareaField from '@js/components/fields/TextareaField.vue';
import FormColumn from '@js/components/forms/FormColumn.vue';
import FormContent from '@js/components/forms/FormContent.vue';
import FormFooter from '@js/components/forms/FormFooter.vue';
import FormRoot from '@js/components/forms/FormRoot.vue';
import TabLayout from '@js/components/tabs/TabLayout.vue';
import { useRoute } from '@js/utils/route';
import { toastError, toastSaved } from '@js/utils/toast';
import { h, reactive, ref } from 'vue';
import { boolean, object, string } from 'yup';

const { tenant, googleAdsKeywordManagement, variableInformation } = defineProps(
    {
        tenant: {
            required: true,
            type: Object,
        },
        googleAdsKeywordManagement: {
            required: false,
            type: Object,
        },
        variableInformation: {
            required: true,
            type: Object,
        },
    },
);

const { route } = useRoute();

const selectedTab = ref();

const form = reactive({
    useConversionValues: googleAdsKeywordManagement
        ? googleAdsKeywordManagement.useConversionValues
        : tenant.type === 'e-commerce',
    variables: {
        desiredValuePerConversion:
            googleAdsKeywordManagement &&
            googleAdsKeywordManagement.variables.desiredValuePerConversion
                ? googleAdsKeywordManagement.variables.desiredValuePerConversion
                : '',
        desiredMaxCostPerConversion:
            googleAdsKeywordManagement &&
            googleAdsKeywordManagement.variables.desiredMaxCostPerConversion
                ? googleAdsKeywordManagement.variables
                      .desiredMaxCostPerConversion
                : '',
        launchAverageCpc:
            googleAdsKeywordManagement &&
            googleAdsKeywordManagement.variables.launchAverageCpc
                ? googleAdsKeywordManagement.variables.launchAverageCpc
                : '',
        cpcMaxCeiling:
            googleAdsKeywordManagement &&
            googleAdsKeywordManagement.variables.cpcMaxCeiling
                ? googleAdsKeywordManagement.variables.cpcMaxCeiling
                : '',
        cpcMinFloor:
            googleAdsKeywordManagement &&
            googleAdsKeywordManagement.variables.cpcMinFloor
                ? googleAdsKeywordManagement.variables.cpcMinFloor
                : '',
        maxCpcLimit:
            googleAdsKeywordManagement &&
            googleAdsKeywordManagement.variables.maxCpcLimit
                ? googleAdsKeywordManagement.variables.maxCpcLimit
                : '',
        startDate:
            googleAdsKeywordManagement &&
            googleAdsKeywordManagement.variables.startDate
                ? googleAdsKeywordManagement.variables.startDate
                : '',
        googleAds: {
            campaign:
                googleAdsKeywordManagement &&
                googleAdsKeywordManagement.variables.googleAds
                    ? googleAdsKeywordManagement.variables.googleAds.campaign
                    : '',
        },
    },
});

const isSubmitting = ref(false);

const validationSchema = object({
    useConversionValues: boolean().required(),
    variables: object().shape({
        desiredValuePerConversion: string().when('useConversionValues', {
            is: () => {
                return form.useConversionValues;
            },
            then: (schema) =>
                schema.required(
                    'La variable "Valeur/Conversion souhaitée" est obligatoire.',
                ),
            otherwise: (schema) => schema.nullable(),
        }),
        desiredMaxCostPerConversion: string().required(
            'La variable "Cout/Conversion max souhaité" est obligatoire.',
        ),
        launchAverageCpc: string().required(
            'La variable "CPC moyen lancement" est obligatoire.',
        ),
        cpcMaxCeiling: string().required(
            'La variable "CPC max plafond" est obligatoire.',
        ),
        cpcMinFloor: string().required(
            'La variable "CPC min plancher" est obligatoire.',
        ),
        maxCpcLimit: string().required(
            'La variable "Limite CPC max" est obligatoire.',
        ),
        startDate: string().nullable(),
        googleAds: object().shape({
            campaign: string().required(
                'La campagne Google Ads est obligatoire.',
            ),
        }),
    }),
});

function submit() {
    isSubmitting.value = true;

    axios
        .post(
            route('front.projectManagement.sea.postKeywordManagement', {
                tenant: tenant.id,
                ...(googleAdsKeywordManagement
                    ? {
                          googleAdsKeywordManagement:
                              googleAdsKeywordManagement.id,
                      }
                    : {}),
            }),
            {
                useConversionValues: form.useConversionValues,
                variables: form.variables,
            },
        )
        .then(() => {
            toastSaved();

            router.visit(
                route(
                    'front.projectManagement.sea.keywordManagements',
                    tenant ? { tenant: tenant.id } : {},
                ),
            );
        })
        .finally(() => {
            isSubmitting.value = false;
        })
        .catch((error) => {
            toastError(error);

            router.visit(
                route('front.projectManagement.sea.keywordManagements', {
                    tenant: tenant.id,
                }),
            );
        });
}
</script>

<template>
    <FormRoot
        v-slot="{ errors }"
        :validation-schema="validationSchema"
        :submit="submit"
    >
        <TabLayout
            v-model:selected-tab="selectedTab"
            :tabs="{
                configuration: {
                    title: 'Configuration',
                    items: {
                        variables: {
                            title: 'Variables',
                            contentSlotName: 'variables',
                            forceMount: true,
                        },
                        googleAds: {
                            title: 'Google Ads',
                            contentSlotName: 'googleAds',
                            forceMount: true,
                        },
                    },
                    forceMount: true,
                },
                ...(googleAdsKeywordManagement
                    ? {
                          keywords: {
                              title: 'Mots-clés',
                              content: h(KeywordManagementItemTable, {
                                  tenant,
                                  googleAdsKeywordManagement,
                              }),
                          },
                      }
                    : {}),
            }"
        >
            <template #variables>
                <FormColumn>
                    <FormContent>
                        <SwitchField
                            v-model="form.useConversionValues"
                            field-name="useConversionValues"
                            field-id="useConversionValues"
                            label="Utiliser les valeurs de conversion"
                        />
                    </FormContent>

                    <FormContent>
                        <div class="mb-4 flex justify-end">
                            <DisplayVariablesAction
                                title="Variables"
                                :variables="variableInformation"
                            >
                                <template #trigger>
                                    <ActionButton type="secondary" size="small">
                                        Variables
                                    </ActionButton>
                                </template>
                            </DisplayVariablesAction>
                        </div>

                        <TextareaField
                            v-if="form.useConversionValues"
                            v-model="form.variables.desiredValuePerConversion"
                            label="Valeur conversion/Conversion souhaitée"
                            field-name="variables.desiredValuePerConversion"
                            field-id="variables.desiredValuePerConversion"
                            required
                            resize="vertical"
                            rows="2"
                        />

                        <TextareaField
                            v-model="form.variables.desiredMaxCostPerConversion"
                            label="Coût/Conversion max souhaité"
                            field-name="variables.desiredMaxCostPerConversion"
                            field-id="variables.desiredMaxCostPerConversion"
                            required
                            resize="vertical"
                            rows="2"
                        />

                        <TextareaField
                            v-model="form.variables.launchAverageCpc"
                            label="CPC moyen lancement"
                            field-name="variables.launchAverageCpc"
                            field-id="variables.launchAverageCpc"
                            required
                            resize="vertical"
                            rows="2"
                        />

                        <TextareaField
                            v-model="form.variables.cpcMaxCeiling"
                            label="CPC max plafond"
                            field-name="variables.cpcMaxCeiling"
                            field-id="variables.cpcMaxCeiling"
                            required
                            resize="vertical"
                            rows="2"
                        />

                        <TextareaField
                            v-model="form.variables.cpcMinFloor"
                            label="CPC min plancher"
                            field-name="variables.cpcMinFloor"
                            field-id="variables.cpcMinFloor"
                            required
                            resize="vertical"
                            rows="2"
                        />

                        <TextareaField
                            v-model="form.variables.maxCpcLimit"
                            label="Limite CPC max"
                            field-name="variables.maxCpcLimit"
                            field-id="variables.maxCpcLimit"
                            required
                            resize="vertical"
                            rows="2"
                        />

                        <InputField
                            v-model="form.variables.startDate"
                            label="Date de début"
                            field-name="variables.startDate"
                            field-id="variables.startDate"
                            type="date"
                        />
                    </FormContent>
                </FormColumn>
            </template>

            <template #googleAds>
                <FormContent>
                    <InputField
                        v-model="form.variables.googleAds.campaign"
                        label="Campagne"
                        field-name="variables.googleAds.campaign"
                        field-id="variables.googleAds.campaign"
                        required
                    />
                </FormContent>
            </template>
        </TabLayout>

        <FormFooter
            v-if="selectedTab !== 'keywords'"
            :is-submitting="isSubmitting"
            :errors="errors"
        >
            <template #actions>
                <DeleteAction
                    v-if="googleAdsKeywordManagement"
                    :url="
                        route(
                            'front.projectManagement.sea.deleteKeywordManagement',
                            {
                                tenant: tenant.id,
                                googleAdsKeywordManagement:
                                    googleAdsKeywordManagement.id,
                            },
                        )
                    "
                    :callback="
                        () => {
                            router.visit(
                                route(
                                    'front.projectManagement.sea.keywordManagements',
                                    {
                                        tenant: tenant.id,
                                    },
                                ),
                            );
                        }
                    "
                >
                    <template #trigger>
                        <ActionButton type="secondary">
                            Supprimer
                        </ActionButton>
                    </template>
                </DeleteAction>
            </template>
        </FormFooter>
    </FormRoot>
</template>
